﻿<script setup lang="ts">
    import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
    import { createChangeDocumentCode } from '@/shared/codeedit/changeDocument.ts';
    import { codeEditFieldsDiff } from '@/shared/codeedit/codeEditFields.ts';
    import _ from 'lodash';
    import {
        BatchDocument,
        BatchDocumentCode,
        createChangeDoc,
        createChangeDocAndCommit,
        createChangeDocAndCommitCodesets,
        createReleaseChain,
        CreateReleaseChainRequest,
        ReleaseAutoSubmitResult,
        ResponsePromise
    } from '@/shared/helpers/api.ts';
    import { http } from '@/shared/httpWrapper';
    import useShow from '../composables/useShow';
    import CodeEditSaveProgress from '@/shared/components/CodeEditSaveProgress.vue';
    import { CanEditCodeOptions, requireCanEditCode } from '@/shared/directives/requirePermission';
    import { CustomCommitCallback } from '@/shared/codeedit/editSessionTypes.ts';
    import debounce from 'lodash/debounce';
    import { IEditSessionState } from '@/shared/codeedit/editSessionState.ts';

    defineSlots<{
        header(): any;
        default(): any;
        footer: any;
    }>();

    const props = defineProps<{
        editSessionState: IEditSessionState;
        beforeSaveCode?: () => Promise<void>;
        libReleaseOrder?: string[];
        useReleaseChain: boolean;
        multipleCodeSetsToRelease?: string[];
        quickCommit?: boolean;
        submitButtonLabel: string;
        canEditCodeOptions: CanEditCodeOptions;
        customCommit?: CustomCommitCallback;
        useBoxRender?: boolean;
    }>();

    const emit = defineEmits<{
        refresh: [payload: BatchDocumentCode[]]; // emits all the batch documents that were created in the edit context
        cancel: [];
        loaded: [];
    }>();

    const vRequireCanEditCode = requireCanEditCode;
    const saveLoading = ref(false);
    const isReleaseEmpty = ref(false);
    const saveError = ref<Error | null>(null);
    const saveResultReleaseId = ref<number | null>(null);
    const saveCodesPayload = ref<BatchDocumentCode[] | null>(null);
    const show = useShow();

    async function saveCode() {
        saveLoading.value = true;
        try {
            if (props.beforeSaveCode) {
                await props.beforeSaveCode();
            }
            const res = await createChange();
            if (res.data.message) {
                show.showInfo(res.data.message);
            }
            if (res.data.releaseId > 0) {
                saveResultReleaseId.value = res.data.releaseId;
            } else {
                isReleaseEmpty.value = true;
            }
        } catch (err) {
            if (err instanceof Error) {
                saveError.value = err;
            }
        } finally {
            saveLoading.value = false;
        }
    }

    function createBatchDocument(): BatchDocument {
        const result = props.editSessionState.applyToAllCodes((code, attributes) => {
            if (!code.metadata.isNew && !codeEditFieldsDiff(code.modified, code.original, attributes)) {
                return undefined;
            }
            return createChangeDocumentCode(attributes, code.modified, code.metadata.isNew ? null : code.original);
        });
        if (_.uniq(result.map((x) => x.cs)).length !== 1) {
            throw new Error('only one distinct codeset can be provided');
        }
        return {
            codeSetName: result[0].cs,
            codes: result.map((x) => x.result)
        };
    }

    function createChange(): ResponsePromise<ReleaseAutoSubmitResult> {
        if (props.customCommit) {
            const doc = createBatchDocument();
            return props.customCommit(doc);
        } else if (props.multipleCodeSetsToRelease && props.multipleCodeSetsToRelease?.length > 0) {
            // edit one codeset, but publish to multiple
            const doc = createBatchDocument();
            const changes = props.multipleCodeSetsToRelease.map((cs): BatchDocument => {
                return {
                    codeSetName: cs,
                    codes: doc.codes
                };
            });
            return createChangeDocAndCommitCodesets(http, changes);
        } else if (props.useReleaseChain || props.editSessionState.numCodeSetsWithChanges.value > 1) {
            const data = prepareReleaseChainData();
            saveCodesPayload.value = data.flatMap((d) => d.codes);
            return createReleaseChain(http, data);
        } else if (props.quickCommit) {
            const doc = createBatchDocument();
            saveCodesPayload.value = doc.codes;
            return createChangeDocAndCommit(http, doc.codeSetName, doc.codes);
        } else {
            const doc = createBatchDocument();
            saveCodesPayload.value = doc.codes;
            return createChangeDoc(http, doc.codeSetName, doc.codes);
        }
    }

    // change documents for multiple libs and codesets
    function prepareReleaseChainData(): CreateReleaseChainRequest {
        const chain: CreateReleaseChainRequest = [];
        const result = props.editSessionState.applyToAllCodes((code, attributes) => {
            // Skip codes that are not new and have no changes — avoids publishing to unmodified relation libraries
            if (!code.metadata.isNew && !codeEditFieldsDiff(code.modified, code.original, attributes)) {
                return undefined;
            }
            return createChangeDocumentCode(attributes, code.modified, code.metadata.isNew ? null : code.original);
        });
        const libsToReleaseTo = props.libReleaseOrder ?? _.uniq(result.map((x) => x.lib));
        for (const lib of libsToReleaseTo) {
            const codesForLib = result.filter((r) => r.lib === lib);
            const groupedByCodeset = Object.groupBy(codesForLib, (x) => x.cs);
            for (const cs in groupedByCodeset) {
                const r = groupedByCodeset[cs];
                if (r) {
                    chain.push({
                        codeSetName: cs,
                        libraryName: lib,
                        codes: r.map((x) => x.result)
                    });
                }
            }
        }
        return chain;
    }

    function onSaveProgressSuccess() {
        emit('refresh', saveCodesPayload.value ?? []);
    }

    function onSaveProgressClose() {
        emit('cancel');
    }

    const checkIdentities = debounce(async () => {
        await props.editSessionState.checkIdentities();
    }, 500);

    const validateInputs = debounce(() => {
        props.editSessionState.validateInputs();
    }, 200);

    const checkUniqueness = debounce(async () => {
        await props.editSessionState.checkUniqueness();
    }, 200);

    watch(props.editSessionState.collection, checkIdentities, { deep: true });

    watch(props.editSessionState.collection, validateInputs, { deep: true });

    watch(props.editSessionState.collection, checkUniqueness, { deep: true });

    onMounted(async () => {
        await checkIdentities();
        await checkUniqueness();
        validateInputs();
    });
    onBeforeUnmount(() => {
        checkIdentities.cancel();
        checkUniqueness.cancel();
        validateInputs.cancel();
    });
</script>

<template>
    <div
        :class="useBoxRender ? 'box' : ''">
        <slot name="header" />
        <div>
            <slot name="default">
                Default
            </slot>
            <code-edit-save-progress
                :save-process-started="saveLoading"
                :release-id="saveResultReleaseId"
                :external-error="saveError"
                :is-release-empty="isReleaseEmpty"
                @success="onSaveProgressSuccess"
                @close="onSaveProgressClose" />
        </div>
        <slot name="footer">
            <div class="field is-grouped is-grouped-right has-margin-top2">
                <p class="control">
                    <b-button
                        @click="() => editSessionState.clearChanges()">
                        <b-icon
                            icon="restore"
                            size="is-small" />
                        <span>Clear changes</span>
                    </b-button>
                </p>
                <p class="control">
                    <b-button
                        @click="emit('cancel')">
                        <b-icon
                            icon="close-circle"
                            size="is-small" />
                        <span>Cancel</span>
                    </b-button>
                </p>
                <p class="control">
                    <b-button
                        v-require-can-edit-code="props.canEditCodeOptions"
                        type="is-primary"
                        :disabled="!editSessionState.canSubmit.value"
                        :class="{'is-loading': saveLoading}"
                        @click="saveCode">
                        <b-icon
                            icon="floppy"
                            size="is-small" />
                        <span>{{ submitButtonLabel }}</span>
                    </b-button>
                </p>
            </div>
        </slot>
    </div>
</template>

<style scoped>
</style>
